import {groupBy} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {Measurement} from 'constants/MeasurementConstants';
import TagValueViewer from 'metadata/TagValueViewer';
import RoleDisplay from 'samples/experimental-roles/ExperimentalRoleDisplay';
import {
  DisplayPlateMapWell,
  isAccessibleTestOrderInfo,
  PlateMapBioentity,
  ReagentType,
  TestOrderInfoById,
} from 'samples/plate-maps/PlateMapConstants';
import {
  flattenSources,
  getDisplayTestDilutionFactor,
  getTestOrderInfosForWell,
} from 'samples/plate-maps/PlateMapUtil';
import {MeasurementWithErrors} from 'samples/plate-maps/transfer-table/validation/FillPlateTransferTableValidationConstants';
import {formatGridCoordinates} from 'samples/SampleUtil';
import {getMeasurementString, measurementsMatch} from 'samples/transfers/TransferUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import ChipPopoverContent from 'ui-library/chips/ChipPopoverContent';

interface PlateMapWellPopoverContentProps {
  well: DisplayPlateMapWell;
  /** Whether to show the _final_ concentrations and volume in the well popover.
   * If false, the _current_ concentrations and volume will be used. */
  showFinalMeasurements: boolean;
  /** Mapping of data parameter version IDs to their display names. */
  dataParameterNamesByVersionId: {[versionId: string]: string};
  testOrderDilutionInfoById?: TestOrderInfoById;
}

function getMeasurementIfValid(
  measurementWithErrors: MeasurementWithErrors<Measurement>
): Measurement | null {
  if (measurementWithErrors.errors.unitsError == null && measurementWithErrors.errors.valueError == null) {
    return measurementWithErrors.measurement;
  }
  return null;
}

function getConcentration(bioentity: PlateMapBioentity, showFinalMeasurements: boolean): Measurement | null {
  if (showFinalMeasurements) {
    return getMeasurementIfValid(bioentity.finalConcentration);
  }
  return bioentity.type === ReagentType.WELL_CONTENT
    ? bioentity.initialConcentration
    : getMeasurementIfValid(bioentity.sourceConcentration);
}

function getQuantity(well: DisplayPlateMapWell, showFinalMeasurements: boolean): Measurement | null {
  return showFinalMeasurements ? getMeasurementIfValid(well.finalQuantity) : well.quantity;
}

/** Render a single well content chip and concentration. */
export function WellContent({
  id,
  concentration,
}: {
  id: string;
  concentration: Measurement | null;
}): JSX.Element {
  return (
    <div className={css(styles.content)}>
      <BenchChip size={ChipSize.MINI} id={id} />
      {concentration != null && concentration.value != null && concentration.unit != null && (
        <span className={css(styles.contentConcentration)}>{`(${getMeasurementString(concentration)})`}</span>
      )}
    </div>
  );
}

/** Content to show in the popover when hovering over a well of a plate map. */
export default function PlateMapWellPopoverContent({
  well,
  showFinalMeasurements,
  dataParameterNamesByVersionId,
  testOrderDilutionInfoById,
}: PlateMapWellPopoverContentProps): JSX.Element {
  const wellContents = useMemo(() => {
    // NOTE: In practice, sources is always empty when showFinalMeasurements = false, but treating both
    // situations the same allows us to have a single code path.
    const allBioentitiesById = groupBy<PlateMapBioentity, string>(
      [...well.contents, ...flattenSources(well)],
      (bioentity): string => bioentity.id
    );
    return Object.entries(allBioentitiesById).map(([id, bioentities]) => {
      const concentrations = bioentities.map((bioentity) =>
        getConcentration(bioentity, showFinalMeasurements)
      );
      return {
        id,
        concentration: measurementsMatch(concentrations) ? concentrations[0] : null,
      };
    });
  }, [well, showFinalMeasurements]);

  const testOrderDilutionFields = useMemo(() => {
    // If there is no test order info, simply omit the fields entirely. This can mean either that we are
    // outside the appropriate sample testing study context, or that test orders/dilutions have not been
    // assigned for any wells in the plate at all.
    // We do this rather than pass through the study design type because this is deep in the callstack & hard
    // to guarantee will be correctly passed through all the layers, as well as to hide it if dilution is
    // irrelevant for the entire plate.
    // Note that as implemented this does mean if every single test order is inaccessible (all contents are
    // inaccessible), we don't have any test order info, and thus we don't return those fields (rather than
    // doing "(No access)"), which seems acceptable / lower priority to fix.
    if (
      !BenchlingConfig.SAMPLE_TESTS_ENABLE ||
      testOrderDilutionInfoById == null ||
      Object.keys(testOrderDilutionInfoById).length === 0
    ) {
      return [];
    }
    const testOrderInfos = getTestOrderInfosForWell(well, testOrderDilutionInfoById ?? {});
    const uniqueDilutionFactors = [
      ...new Set(testOrderInfos.map((info) => getDisplayTestDilutionFactor(info))),
    ];
    return [
      {
        label: 'Test dilution factor',
        value: uniqueDilutionFactors.length > 0 ? uniqueDilutionFactors.join(', ') : 'N/A',
      },
      {
        label: 'Test orders',
        value: testOrderInfos
          .map((testOrder) => (isAccessibleTestOrderInfo(testOrder) ? testOrder.name : '(No access)'))
          .join(', '),
      },
    ];
  }, [well, testOrderDilutionInfoById]);

  const fields = [
    {
      label: 'Contents',
      value:
        wellContents.length === 0 ? (
          <span>Empty</span>
        ) : (
          <div>
            {wellContents.map((wellContent) => (
              <WellContent key={wellContent.id} {...wellContent} />
            ))}
          </div>
        ),
    },
    {
      label: 'Well volume',
      value: getMeasurementString(getQuantity(well, showFinalMeasurements)),
    },
    {
      label: 'Well role',
      value: well.role == null ? 'Not specified' : <RoleDisplay role={well.role} />,
    },
    ...testOrderDilutionFields,
    ...[...Object.entries(well.schemaTags), ...Object.entries(well.dataParameterTags)].map(
      ([versionId, tag]) => ({
        generateStableKey: () => versionId,
        label: dataParameterNamesByVersionId[versionId] ?? tag.tagSchemaField.name,
        value: (
          <TagValueViewer
            tag={tag}
            chipSize={ChipSize.MINI}
            shouldPreviewImages={true}
            showChipsAsLinks={true}
            summarize={true}
            summarizeThreshold={3}
            shouldOpenLinkInNewTab={true}
          />
        ),
      })
    ),
  ];

  return (
    <div {...getTestProps({component: 'PlateMapWellPopoverContent', element: 'root'})}>
      <h4>{formatGridCoordinates(well.position.row, well.position.column)}</h4>
      <ChipPopoverContent info={{fields}} />
    </div>
  );
}

const styles = StyleSheet.create({
  content: {
    display: 'flex',
    marginBottom: '4px',
  },
  contentConcentration: {
    flex: 'none',
    margin: 'auto 0',
  },
});
